import { useCallback, type ReactNode } from "react";
import { ApiProvider } from "@repo/api-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";

/**
 * Bridges the shared AuthContext into the shared `<ApiProvider>`. Wrapping the
 * subtree means every shared hook automatically picks up token refreshes via
 * `useAuth().getIdToken`.
 *
 * `baseUrl` is passed by each app (web and desktop resolve it from their own
 * build-time env) rather than read here, so this stays platform-agnostic.
 */
export function AppApiProvider({
  baseUrl,
  children,
}: {
  baseUrl: string;
  children: ReactNode;
}): ReactNode {
  const { getIdToken, invalidateSession } = useAuth();
  const queryClient = useQueryClient();
  // Pin the underlying ref so ApiProvider doesn't rebuild the openapi-fetch
  // client on every render of the auth context.
  const stableGetIdToken = useCallback(() => getIdToken(), [getIdToken]);
  const handleUnauthorized = useCallback(() => {
    queryClient.clear();
    invalidateSession();
  }, [invalidateSession, queryClient]);
  return (
    <ApiProvider
      baseUrl={baseUrl}
      getIdToken={stableGetIdToken}
      onUnauthorized={handleUnauthorized}
    >
      {children}
    </ApiProvider>
  );
}
